// ImageNode: <figure data-type="image"><img src=… alt=… [width=…]></figure>
//
// DecoratorNode を継承し「atomic な単一ノード」として扱う。
// Lexical reconciler は DecoratorNode の内部 DOM を touch しないので、
// <img> や リサイズハンドル <span> を自由に組み込める。

import { DecoratorNode } from "lexical"

export class ImageNode extends DecoratorNode {
  static getType() { return "image" }

  static clone(node) {
    return new ImageNode(node.__src, node.__alt, node.__width, node.__key)
  }

  constructor(src, alt = "", width = null, key) {
    super(key)
    this.__src   = src
    this.__alt   = alt || ""
    this.__width = width // 数値 (px) or null
  }

  // 属性 getter / setter
  getSrc()   { return this.getLatest().__src }
  getAlt()   { return this.getLatest().__alt }
  getWidth() { return this.getLatest().__width }

  setAlt(alt) { this.getWritable().__alt = alt || "" }
  setWidth(w) {
    const n = (typeof w === "number") ? Math.round(w) : null
    this.getWritable().__width = (n && n > 0) ? n : null
  }

  // block レベル
  isInline()             { return false }
  isKeyboardSelectable() { return true }

  // DOM 生成 (画面に出す)
  // figure ごと contentEditable=false にして、内側にリサイズハンドルを 4 隅に置く。
  // image_resize.js プラグインが click/mousedown ハンドラを attach する。
  createDOM(_config) {
    const figure = document.createElement("figure")
    figure.setAttribute("data-type", "image")
    figure.setAttribute("data-lexical-image-node", "")
    figure.contentEditable = "false"

    const img = document.createElement("img")
    img.src = this.__src
    img.alt = this.__alt
    if (this.__width) img.width = this.__width
    img.draggable = false
    img.loading   = "lazy"
    figure.appendChild(img)

    for (const corner of ["nw", "ne", "sw", "se"]) {
      const h = document.createElement("span")
      h.className = `akebono-image-handle akebono-image-handle-${corner}`
      h.dataset.handle = corner
      h.setAttribute("aria-hidden", "true")
      figure.appendChild(h)
    }
    return figure
  }

  updateDOM(prevNode, figure) {
    const img = figure.querySelector("img")
    if (!img) return false
    if (prevNode.__src !== this.__src)     img.src = this.__src
    if (prevNode.__alt !== this.__alt)     img.alt = this.__alt
    if (prevNode.__width !== this.__width) {
      if (this.__width) img.width = this.__width
      else img.removeAttribute("width")
    }
    return false
  }

  // DecoratorNode required: vanilla なので使われないが定義する
  decorate(_editor, _config) {
    return null
  }

  // HTML import (DOMParser からの取り込み)
  // <figure data-type="image"><img></figure> も、裸の <img> も両方拾う。
  static importDOM() {
    return {
      figure: (node) => {
        if (node.getAttribute("data-type") !== "image") return null
        return {
          conversion: (el) => {
            const img = el.querySelector("img")
            if (!img) return { node: null }
            return {
              node: $createImageNode(
                img.getAttribute("src") || "",
                img.getAttribute("alt") || "",
                parseWidth(img.getAttribute("width"))
              )
            }
          },
          priority: 2,
        }
      },
      img: (node) => {
        if (node.parentElement?.getAttribute("data-type") === "image") return null
        return {
          conversion: (el) => ({
            node: $createImageNode(
              el.getAttribute("src") || "",
              el.getAttribute("alt") || "",
              parseWidth(el.getAttribute("width"))
            )
          }),
          priority: 1,
        }
      },
    }
  }

  // HTML export ($generateHtmlFromNodes で使われる)
  // ハンドル等の編集 UI は含めず、純粋な <figure><img></figure> だけ返す。
  exportDOM() {
    const figure = document.createElement("figure")
    figure.setAttribute("data-type", "image")

    const img = document.createElement("img")
    img.setAttribute("src", this.__src)
    if (this.__alt)   img.setAttribute("alt", this.__alt)
    if (this.__width) img.setAttribute("width", String(this.__width))
    figure.appendChild(img)
    return { element: figure }
  }

  // JSON シリアライズ (EditorState 永続化用)
  static importJSON(serialized) {
    return $createImageNode(serialized.src, serialized.alt, serialized.width)
  }
  exportJSON() {
    return {
      type: "image",
      src: this.__src,
      alt: this.__alt,
      width: this.__width,
      version: 1,
    }
  }
}

function parseWidth(raw) {
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function $createImageNode(src, alt = "", width = null) {
  return new ImageNode(src, alt, width)
}

export function $isImageNode(node) {
  return node instanceof ImageNode
}
